import React from 'react';
import PropTypes from 'prop-types';
import Pressable from "react-native-web/dist/exports/Pressable";
import Platform from "react-native-web/dist/exports/Platform";
import StyleSheet from "react-native-web/dist/exports/StyleSheet";
import View from "react-native-web/dist/exports/View";
import { applyOuterBorder, useThemeTokensCallback, applyShadowToken } from '../ThemeProvider';
import { a11yProps, clickProps, getTokensPropType, hrefAttrsProp, linkProps, resolvePressableState, selectSystemProps, selectTokens, variantProp, viewProps, withLinkRouter } from '../utils';
import Icon from '../Icon';

// function to get the dimentionals of the conditionals
import { jsx as _jsx } from "react/jsx-runtime";
const getPasswordDimensions = (password, width, height) => {
  return password ? {
    width,
    height
  } : {};
};
const [selectProps, selectedSystemPropTypes] = selectSystemProps([a11yProps, viewProps]);
const selectOuterStyle = (_ref, password) => {
  let {
    backgroundColor,
    outerBorderWidth,
    outerBorderColor,
    outerBorderGap,
    borderRadius,
    borderTopLeftRadius,
    borderTopRightRadius,
    borderBottomLeftRadius,
    borderBottomRightRadius,
    shadow,
    width,
    height
  } = _ref;
  return [{
    backgroundColor,
    ...applyShadowToken(shadow),
    ...applyOuterBorder({
      borderRadius,
      borderTopLeftRadius,
      borderTopRightRadius,
      borderBottomLeftRadius,
      borderBottomRightRadius,
      outerBorderWidth,
      outerBorderColor,
      outerBorderGap
    }),
    ...getPasswordDimensions(password, width, height),
    ...Platform.select({
      web: {
        outline: 'none',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center'
      }
    })
  }, staticStyles.outer];
};
const calculatePadding = (padding, borderWidth) => padding && Math.max(0, padding - borderWidth); // Stable size as border changes

const selectInnerStyle = (_ref2, password) => {
  let {
    borderColor,
    borderWidth,
    borderTopLeftRadius,
    borderTopRightRadius,
    borderBottomLeftRadius,
    borderBottomRightRadius,
    borderRadius,
    padding = 0,
    borderTopWidth,
    borderRightWidth,
    borderBottomWidth,
    borderLeftWidth,
    paddingLeft,
    paddingRight,
    paddingTop,
    paddingBottom,
    width,
    height
  } = _ref2;
  return {
    // Inner borders animate with the icon and should be treated like a themable feature of the icon
    borderColor,
    borderRadius,
    borderWidth,
    borderTopLeftRadius,
    borderTopRightRadius,
    borderBottomLeftRadius,
    borderBottomRightRadius,
    borderTopWidth,
    borderRightWidth,
    borderBottomWidth,
    borderLeftWidth,
    padding: calculatePadding(padding, borderWidth),
    paddingLeft: calculatePadding(paddingLeft, borderLeftWidth),
    paddingRight: calculatePadding(paddingRight, borderRightWidth),
    paddingTop: calculatePadding(paddingTop, borderTopWidth),
    paddingBottom: calculatePadding(paddingBottom, borderBottomWidth),
    ...Platform.select({
      web: {
        pointerEvents: 'none',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center'
      }
    }),
    ...getPasswordDimensions(password, width, height)
  };
};

/**
 * A pressable themeless base component that handles pressable states and passes tokens
 * based on these to an outer border and a base Card component. Not intended to be used in
 * apps or sites directly: build themed components on top of this.
 */
const IconButton = /*#__PURE__*/React.forwardRef((_ref3, ref) => {
  let {
    tokens,
    variant = {},
    icon: IconComponent,
    href,
    hrefAttrs,
    testID,
    accessibilityRole = href ? 'link' : 'button',
    ...rawRest
  } = _ref3;
  const {
    onPress,
    ...rest
  } = clickProps.toPressProps(rawRest);
  const selectedProps = selectProps({
    ...rest,
    accessibilityRole
  });
  const handlePress = () => {
    linkProps.handleHref({
      href,
      onPress
    })({
      nativeEvent: {
        target: ref?.current?.id
      }
    });
  };
  const getTokens = useThemeTokensCallback('IconButton', tokens, variant);
  const getOuterStyle = pressableState => selectOuterStyle(getTokens(resolvePressableState(pressableState), variant.password));
  return /*#__PURE__*/_jsx(Pressable, {
    ref: ref,
    href: href,
    onPress: handlePress,
    hrefAttrs: hrefAttrs,
    style: getOuterStyle,
    ...selectedProps,
    testID: testID,
    children: pressableState => {
      const themeTokens = getTokens(resolvePressableState(pressableState));
      return /*#__PURE__*/_jsx(View, {
        style: selectInnerStyle(themeTokens, variant.password),
        children: /*#__PURE__*/_jsx(Icon, {
          icon: IconComponent || themeTokens.icon,
          title: selectedProps.accessibilityLabel,
          tokens: selectTokens('Icon', themeTokens, 'icon'),
          variant: variant
        })
      });
    }
  });
});
IconButton.displayName = 'IconButton';
IconButton.propTypes = {
  ...selectedSystemPropTypes,
  variant: variantProp.propType,
  tokens: getTokensPropType('IconButton'),
  /**
   * A unique identifier for testing purposes.
   * Will be added as a `data-testid` attribute.
   */
  testID: PropTypes.string,
  /**
   * Defines the icon to be rendered
   */
  icon: PropTypes.elementType,
  /**
   * URL to navigate to when the `Iconbutton` is pressed
   */
  href: PropTypes.string,
  /**
   * URL options to navigate to when the `Iconbutton` is pressed
   */
  hrefAttrs: PropTypes.shape(hrefAttrsProp.types),
  /**
   * Function to execute when the `Iconbutton` is pressed
   */
  onPress: PropTypes.func
};
const staticStyles = StyleSheet.create({
  outer: {
    alignSelf: 'baseline'
  }
});
export default withLinkRouter(IconButton);